import { useEffect, useMemo, useState, type FormEvent } from 'react';

import { IconPrinter } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { useIpdDaycareLookup } from './ipd-daycare.queries';
import { currency } from './ipd-daycare.utils';
import type { IpdDaycareAdmission, IpdDaycareLookupOption } from './types';

type RequisitionKind = 'pathology' | 'non-pathology' | 'radiology';
type RequisitionPanelContext = {
  patientName: string;
  uhid: string | null;
  primaryLabel: string;
  primaryValue: string | number;
  secondaryText?: string;
};
type RequisitionPanelCharge = {
  id: string;
  label: string;
  rate: number;
  kind?: RequisitionKind;
};
type IpdRequisitionPanelProps = (
  | { admission: IpdDaycareAdmission; context?: never }
  | { admission?: never; context: RequisitionPanelContext }
) & {
  billCharges?: RequisitionPanelCharge[];
};

const requisitionSections: Array<{ kind: RequisitionKind; label: string }> = [
  { kind: 'pathology', label: 'Pathology' },
  { kind: 'non-pathology', label: 'Non-Pathology' },
  { kind: 'radiology', label: 'Radiology' },
];

const sectionHeading = (label: string) => label === 'Pathology' ? 'Pathology Test' : `${label} Charges`;

const escapeHtml = (value: string | number | null | undefined) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

export const IpdRequisitionPanel = ({ admission, context, billCharges }: IpdRequisitionPanelProps) => {
  const [activeKind, setActiveKind] = useState<RequisitionKind>('pathology');
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const charges = useIpdDaycareLookup('requisition-charges', search, { requisitionSection: activeKind });
  const details = context ?? {
    patientName: admission.patientSnapshot.name,
    uhid: admission.patientSnapshot.uhid,
    primaryLabel: 'IPD No',
    primaryValue: admission.ipdNumber,
    secondaryText: admission.admissionType,
  };
  const activeSectionLabel = requisitionSections.find((section) => section.kind === activeKind)?.label ?? 'Pathology';
  const activeHeading = sectionHeading(activeSectionLabel);
  const billChargeMap = useMemo(() => new Map((billCharges ?? []).map((charge) => [charge.id, charge])), [billCharges]);
  const rows = useMemo<IpdDaycareLookupOption[]>(() => {
    if (billCharges) {
      const normalizedSearch = search.trim().toLowerCase();
      return billCharges
        .filter((charge) => !charge.kind || charge.kind === activeKind)
        .filter((charge) => !normalizedSearch || charge.label.toLowerCase().includes(normalizedSearch))
        .map((charge) => ({
          id: charge.id,
          label: charge.label,
          rate: charge.rate,
          requisitionSection: { id: charge.kind ?? activeKind, name: requisitionSections.find((section) => section.kind === (charge.kind ?? activeKind))?.label ?? activeSectionLabel },
          requisitionKind: charge.kind ?? activeKind,
        }));
    }
    const lookupRows = charges.data ?? [];
    return lookupRows
      .filter((charge) => billChargeMap.size === 0 || billChargeMap.has(charge.id))
      .map((charge) => {
        const billCharge = billChargeMap.get(charge.id);
        return billCharge ? { ...charge, label: billCharge.label, rate: billCharge.rate } : charge;
      });
  }, [activeKind, activeSectionLabel, billChargeMap, billCharges, charges.data, search]);
  const selectedRows = useMemo(() => rows.filter((charge) => selectedIds.has(charge.id)), [rows, selectedIds]);
  const allVisibleSelected = rows.length > 0 && rows.every((charge) => selectedIds.has(charge.id));

  useEffect(() => { setSelectedIds(new Set()); }, [activeKind, search]);

  const applySearch = (event: FormEvent) => {
    event.preventDefault();
    setSearch(searchDraft.trim());
  };

  const resetSearch = () => {
    setSearchDraft('');
    setSearch('');
  };

  const toggleCharge = (chargeId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(chargeId)) next.delete(chargeId);
      else next.add(chargeId);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds((current) => {
      if (allVisibleSelected) return new Set();
      const next = new Set(current);
      rows.forEach((charge) => next.add(charge.id));
      return next;
    });
  };

  const printSelected = () => {
    if (selectedRows.length === 0) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.opener = null;
    const printedAt = new Date().toLocaleString();
    const lineRows = selectedRows.map((charge: IpdDaycareLookupOption, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${escapeHtml(charge.label)}</td>
        <td>${escapeHtml(charge.requisitionSection?.name ?? activeSectionLabel)}</td>
        <td class="num">${escapeHtml(currency(charge.rate ?? 0))}</td>
      </tr>
    `).join('');
    printWindow.document.write(`<!doctype html>
      <html>
        <head>
          <title>${escapeHtml(activeHeading)}</title>
          <style>
            body { margin: 24px; color: #052f2e; font-family: Arial, sans-serif; font-size: 12px; }
            h1 { margin: 0 0 4px; padding-left: 8px; border-left: 4px solid #0d6b66; font-size: 18px; }
            .meta { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px 20px; margin: 12px 0 16px; }
            .meta div { font-weight: 700; }
            .meta span { color: #425f5c; font-weight: 400; }
            table { width: 100%; border-collapse: collapse; }
            th { padding: 8px; color: #fff; background: #285f59; text-align: left; font-size: 11px; text-transform: uppercase; }
            td { padding: 7px 8px; border: 1px solid #cfe1dd; }
            td:first-child { width: 48px; text-align: center; background: #edf8f2; font-weight: 700; }
            .num { text-align: right; white-space: nowrap; }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(activeHeading)}</h1>
          <section class="meta">
            <div>Patient: <span>${escapeHtml(details.patientName)}</span></div>
            <div>UHID: <span>${escapeHtml(details.uhid ?? '-')}</span></div>
            <div>${escapeHtml(details.primaryLabel)}: <span>${escapeHtml(details.primaryValue)}</span></div>
            <div>Printed: <span>${escapeHtml(printedAt)}</span></div>
          </section>
          <table>
            <thead><tr><th>#</th><th>Charge Name</th><th>Requisition Section</th><th>Rate</th></tr></thead>
            <tbody>${lineRows}</tbody>
          </table>
        </body>
      </html>`);
    printWindow.document.close();
    printWindow.focus();
    window.setTimeout(() => printWindow.print(), 250);
  };

  return (
    <div className="ipd-requisition-panel">
      <header className="opd-bill-banner">
        <div className="opd-bill-banner-row"><span>Requisition - {details.patientName}</span></div>
        <p>{details.primaryLabel} {details.primaryValue}{details.secondaryText ? ` - ${details.secondaryText}` : ''}</p>
      </header>

      <section className="opd-bill-subsection">
        <div className="ipd-requisition-toolbar">
          <div className="ipd-requisition-tabs" role="tablist" aria-label="Requisition sections">
            {requisitionSections.map((section) => (
              <button
                key={section.kind}
                type="button"
                role="tab"
                aria-selected={activeKind === section.kind}
                className={`ipd-requisition-tab${activeKind === section.kind ? ' active' : ''}`}
                onClick={() => { setActiveKind(section.kind); resetSearch(); }}
              >
                <span>{section.label}</span>
              </button>
            ))}
          </div>

          <form className="ipd-requisition-search" onSubmit={applySearch}>
            <input
              value={searchDraft}
              placeholder="Search charge"
              onChange={(event) => setSearchDraft(event.target.value)}
            />
            <button className="button button-primary" type="submit">Search</button>
            <button className="button button-secondary" type="button" onClick={resetSearch}>Reset</button>
          </form>
        </div>
      </section>

      <section className="opd-section">
        <div className="ipd-requisition-section-head">
          <h2>{activeHeading}</h2>
          <button className="button button-secondary" type="button" disabled={selectedRows.length === 0} onClick={printSelected}>
            <IconPrinter /> Print Selected ({selectedRows.length})
          </button>
        </div>
        {!billCharges && charges.isLoading ? <LoadingState /> : null}
        {!billCharges && charges.isError ? <ErrorState title="Unable to load requisition charges" message="Please try again." action={{ label: 'Try again', onClick: () => void charges.refetch() }} /> : null}
        {(billCharges || charges.isSuccess) && rows.length === 0 ? <EmptyState title="No charges found" message={billCharges ? 'No bill detail charges found for this requisition section.' : 'No active charges are mapped to this requisition section.'} /> : null}
        {(billCharges || charges.isSuccess) && rows.length > 0 ? (
          <div className="table-scroll">
            <table className="opd-charge-table ipd-requisition-charge-table">
              <thead>
                <tr>
                  <th className="ipd-requisition-check-col">
                    <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} aria-label={`Select all ${activeSectionLabel} charges`} />
                  </th>
                  <th className="opd-charge-narrow">#</th>
                  <th>Charge Name</th>
                  <th>Requisition Section</th>
                  <th className="print-bill-col-num">Rate</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((charge, index) => (
                  <tr key={charge.id}>
                    <td className="ipd-requisition-check-col">
                      <input type="checkbox" checked={selectedIds.has(charge.id)} onChange={() => toggleCharge(charge.id)} aria-label={`Select ${charge.label} for print`} />
                    </td>
                    <td className="opd-charge-narrow">{index + 1}</td>
                    <td className="opd-charge-name-cell" title={charge.label}>{charge.label}</td>
                    <td>{charge.requisitionSection?.name ?? activeSectionLabel}</td>
                    <td className="print-bill-col-num">{currency(charge.rate ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
};
